import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { marksCurrent } from '../navCurrent'

export type NavMenuItem = {
  label: string
  to?: string
  /** For actions rather than navigation — signing out, say. */
  onSelect?: () => void
  description?: string
  /** Hidden entirely when false. Capability checks belong to the caller. */
  visible?: boolean
  danger?: boolean
  /**
   * How many of this thing are waiting.
   *
   * Shown beside the label rather than summed onto the closed menu, because a
   * bare number on a menu holding five items says nothing about which of them
   * it means. Next to Conflicts it is unambiguous (D-167).
   */
  count?: number
}

export type NavMenuGroup = {
  heading?: string
  items: NavMenuItem[]
}

/**
 * A grouped dropdown for the header.
 *
 * Exists so the nav bar can stay short as the app grows. Everything daily
 * stays a top-level link; setup, administration and account live behind an
 * icon — the once-a-season and the every-evening should not compete for the
 * same attention (D-103).
 *
 * Closes on Escape and on a click outside, and every item is a real link or
 * button so the keyboard works without any of this being reimplemented.
 */
export default function NavMenu({
  label,
  icon,
  groups,
  align = 'right',
  drop = 'down',
  dot,
}: {
  label: string
  /**
   * Optional, and absent on purpose for a menu sitting among the text links.
   * "Studio" is one of the nav items and should read like the others; the gear
   * carries an icon because it is the thing being tucked away (D-167).
   */
  icon?: ReactNode
  groups: NavMenuGroup[]
  align?: 'left' | 'right'
  /**
   * Which way the panel opens.
   *
   * <b>Down was the only option, and the bottom bar is pinned to the bottom of
   * the window.</b> `top-full` put the panel below a trigger that is already at
   * the last pixel of the viewport, so on a phone the owner's *More* opened a
   * menu whose every item — Attendance, People, All classes, Studio, Website —
   * sat off the bottom of the screen. Measured at 390x844: the panel's top edge
   * was at 840 and its first item at 847. Five destinations, unreachable.
   *
   * <b>Nothing could have caught it.</b> The layout sweep only ever sees the
   * first screen at a URL, and this panel is behind a tap; `audit-deep.mjs`
   * drives taps but had no step for it. It was found by a person opening it
   * (D-254, again).
   */
  drop?: 'up' | 'down'
  /**
   * Something inside needs attention.
   *
   * <b>A dot, not a number.</b> A closed menu can only honestly say "there is
   * something in here" — a count on a menu holding five different things does
   * not say which of them it means, and a number nobody can attribute is one
   * people learn to ignore. The counts live on the items, where each has a
   * label beside it (D-167).
   *
   * The mark itself still matters: it is what makes burying something safe
   * (D-103).
   */
  dot?: boolean
}) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  useEffect(() => {
    if (!open) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }

    const onClick = (e: MouseEvent) => {
      if (!container.current?.contains(e.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  // Groups whose every item is hidden must not leave a stray heading or rule.
  const shown = groups
    .map((group) => ({ ...group, items: group.items.filter((i) => i.visible !== false) }))
    .filter((group) => group.items.length > 0)

  if (shown.length === 0) return null

  const item =
    'block w-full rounded-lg px-2.5 py-1.5 text-left text-[13px] no-underline transition-colors'

  const marked = dot === true

  return (
    <div ref={container} className="relative">
      {/*
        Two shapes, decided by whether there is an icon.

        With one, a 32px square that sits with the other icon buttons. Without,
        a text button styled like the NavLinks beside it — because a menu among
        the text links has to read like the text links (D-167).

        The label used to be aria-only, so an icon-less menu rendered as an empty
        square: present, focusable, and invisible.
      */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={marked ? `${label} — needs attention` : label}
        title={marked ? `${label} — needs attention` : label}
        className={
          icon
            ? `relative grid size-8 place-items-center rounded-lg transition-colors ${
                open ? 'bg-accent-soft text-accent-2' : 'text-ink-3 hover:bg-surface-2 hover:text-ink'
              }`
            : `relative flex items-center gap-1 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                open ? 'bg-accent-soft text-accent-2' : 'text-ink-2 hover:text-ink'
              }`
        }
      >
        {icon ?? (
          <>
            {label}
            <span aria-hidden="true" className="text-[10px] leading-none opacity-70">
              &#9662;
            </span>
          </>
        )}

        {marked && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-crit ring-2 ring-surface"
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-50 w-60 rounded-xl border border-line bg-surface p-1.5 shadow-xl ${
            drop === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          } ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {shown.map((group, groupIndex) => (
            <div key={group.heading ?? groupIndex}>
              {groupIndex > 0 && <div className="my-1.5 h-px bg-line" />}

              {group.heading && (
                <p className="px-2.5 pt-1 pb-1.5 text-[10.5px] font-bold tracking-[0.1em] text-ink-3 uppercase">
                  {group.heading}
                </p>
              )}

              {group.items.map((entry) => {
                /*
                 * **One variable, not two calls.** The flag and the highlight
                 * have to be the same answer, and an audit proved that two
                 * calls do not make them so: setting *only* the class
                 * expression to false left the menuitem carrying
                 * `aria-current="page"` with no highlight, and all 2,909 tests
                 * stayed green. The visible half is the half the owner
                 * reported (D-884), so it cannot be the half that can drift.
                 */
                const current = entry.to !== undefined && marksCurrent(pathname, entry.to)

                return entry.to ? (
                  <Link
                    key={entry.label}
                    to={entry.to}
                    role="menuitem"
                    /*
                     * `onSelect` fires here too, not only on the button branch.
                     *
                     * An item may legitimately be both a destination and a
                     * thing worth recording — the portal switch is a link so it
                     * keeps the active highlight (which is half of how the menu
                     * says which side you are on), and needs a callback so the
                     * choice is remembered (D-309). Before this, `to` silently
                     * won and the callback never ran: it navigated correctly
                     * and forgot every time, which is the kind of half-working
                     * that survives a demo.
                     */
                    onClick={() => {
                      setOpen(false)
                      entry.onSelect?.()
                    }}
                    /*
                     * `marksCurrent`, and not `NavLink`'s `isActive` (D-884).
                     *
                     * This was a `NavLink` whose highlight *and* whose
                     * `aria-current` came from its own match - exact, or a prefix at a segment boundary, and never across to another destination — so on
                     * `/admin/staff` the rail lit **People** through the view
                     * map and this copy of People, behind the phone bar's
                     * *More*, lit nothing. One account, one screen, two
                     * answers. The rail and this menu ask one function now.
                     */
                    aria-current={current ? 'page' : undefined}
                    className={`${item} ${
                      current
                        ? 'bg-accent-soft font-semibold text-accent-2'
                        : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
                    }`}
                  >
                    {entry.label}
                    {(entry.count ?? 0) > 0 && (
                      <span className="tabular ml-1.5 rounded-full bg-crit px-1.5 py-0.5 text-[10px] font-bold text-on-crit">
                        {entry.count! > 99 ? '99+' : entry.count}
                      </span>
                    )}
                    {entry.description && (
                      <span className="mt-0.5 block text-[11.5px] text-ink-3">{entry.description}</span>
                    )}
                  </Link>
                ) : (
                  <button
                    key={entry.label}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false)
                      entry.onSelect?.()
                    }}
                    className={`${item} ${
                      entry.danger
                        ? 'text-crit hover:bg-crit-bg'
                        : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
                    }`}
                  >
                    {entry.label}
                    {entry.description && (
                      <span className="mt-0.5 block text-[11.5px] text-ink-3">{entry.description}</span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
